<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useDashboardStore } from '@Stores/dashboard.store';
import { useCompanyStore } from '@Stores/company.store';
import type { DealDashboard } from '@Src/shared/types/dashboard.types';
import BottomSheetModal from '@Components/BottomSheetModal.vue';
import { formatShortDate } from '@Src/shared/utils/formatDate';
import { isPropertyOwnerRole } from '@Src/shared/utils/participantRole';

defineProps<{
    open: boolean;
}>();

const emit = defineEmits<{
    'update:open': [value: boolean];
}>();

const dashboardStore = useDashboardStore();
const companyStore = useCompanyStore();
const router = useRouter();

const search = ref('');

const activeDeal = computed(() => dashboardStore.activeDeal);

// Split a flat "123 Main St, Detroit MI 48201" into street + rest so the
// row can render the prototype's two-line layout.
const parseAddress = (raw?: string | null) => {
    if (!raw) return { street: '', rest: '' };
    const [street, ...rest] = raw.split(',');
    return { street: street.trim(), rest: rest.join(',').trim() };
};

const taskProgress = (deal: DealDashboard) => {
    const completed = deal.completedTasks.length;
    const total = deal.pendingTasks.length + deal.inProgressTasks.length + deal.completedTasks.length;
    return `${completed}/${total} tasks complete`;
};

const dealDisplay = (deal: DealDashboard) => {
    const { street, rest } = parseAddress(deal.propertyAddress);
    const parts: string[] = [];
    if (rest) parts.push(rest);
    const closing = formatShortDate(deal.closingDate);
    if (closing) parts.push(`Closing ${closing}`);

    if (street) {
        return { street, subline: parts.join(' · ') };
    }
    // Address-less deal: show progress so rows are distinguishable in the picker.
    // Task progress goes in the subline (smaller font) to keep the street line clean.
    const progress = taskProgress(deal);
    if (progress) parts.unshift(progress);
    return { street: 'Closing', subline: parts.join(' · ') };
};

// DS-2862 (revised): address-less deals (e-sign-only work, etc.) were previously
// filtered out of the picker entirely because they rendered as indistinguishable
// "No address" rows. Now they display as "Closing — X/Y tasks complete" which
// gives participants enough signal to pick the right deal. All deals are shown.
const filteredDeals = computed(() => {
    const q = search.value.toLowerCase().trim();
    const deals = q
        ? dashboardStore.deals.filter((d) => {
            const label = d.propertyAddress?.toLowerCase() || 'closing';
            return label.includes(q);
        })
        : dashboardStore.deals;
    return deals.map((d) => ({
        ...d,
        display: dealDisplay(d),
    }));
});

const close = () => {
    emit('update:open', false);
    search.value = '';
};

const selectDeal = (dealId: string) => {
    dashboardStore.selectDeal(dealId);
    const slug = companyStore.companySlug ?? '';
    router.push({ name: 'deal-tasks', params: { companySlug: slug, dealId } });
    close();
};
</script>

<template>
    <BottomSheetModal
        :open="open"
        dialog-label="Select property"
        @update:open="(val: boolean) => { if (!val) close(); }"
    >
        <div class="property-sheet portal-step-enter">
            <h2 class="property-sheet__title">Select property</h2>

            <div class="property-sheet__search">
                <svg class="property-sheet__search-icon" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                </svg>
                <input
                    v-model="search"
                    type="text"
                    class="property-sheet__search-input"
                    placeholder="Search properties..."
                    aria-label="Search properties"
                />
            </div>

            <!-- Plain list semantics — Enter/Space activate a focused row,
                 but full role="listbox" would also require arrow-key nav with
                 a roving tabindex, which we haven't built. Selection state is
                 surfaced via the check icon's accessible name on the active
                 row. -->
            <ul class="property-sheet__list">
                <li v-if="filteredDeals.length === 0" class="property-sheet__empty">
                    No properties found.
                </li>

                <li
                    v-for="deal in filteredDeals"
                    :key="deal.dealId"
                    class="property-sheet__item"
                    role="button"
                    tabindex="0"
                    @click="selectDeal(deal.dealId)"
                    @keydown.enter.prevent="selectDeal(deal.dealId)"
                    @keydown.space.prevent="selectDeal(deal.dealId)"
                >
                    <!-- DS-2743: only show house icon when the signed-in participant is
                         a property-owning role on that deal (Buyer/Seller). The icon
                         hints at the user's stake in the property; rendering it for
                         agents/attorneys/lenders is visual noise.
                         DS-2874: when the icon is suppressed by the role gate, render
                         an aria-hidden spacer of the same width so the street/subline
                         column stays aligned across rows. Otherwise the affected row
                         shifts 34px left (icon width + flex gap) relative to its
                         neighbors, which Luis flagged as a visual regression. -->
                    <app-icon
                        v-if="isPropertyOwnerRole(deal.participantRole) || !deal.propertyAddress?.trim()"
                        icon="cd-ic-home"
                        class="property-sheet__item-icon"
                    />
                    <span
                        v-else
                        class="property-sheet__item-icon property-sheet__item-icon--placeholder"
                        aria-hidden="true"
                    />
                    <div class="property-sheet__item-info">
                        <span class="property-sheet__item-street">{{ deal.display.street }}</span>
                        <span v-if="deal.display.subline" class="property-sheet__item-subline">
                            {{ deal.display.subline }}
                        </span>
                    </div>
                    <svg
                        v-if="deal.dealId === activeDeal?.dealId"
                        class="property-sheet__check"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                        role="img"
                        aria-label="Currently selected"
                    >
                        <path d="M5 13l4 4L19 7" />
                    </svg>
                </li>
            </ul>
        </div>
    </BottomSheetModal>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.property-sheet {
    display: flex;
    flex-direction: column;
    // Cancel BottomSheetModal's content padding on all four sides so the
    // search bar's bottom-border spans edge-to-edge and the list bottoms
    // out at the sheet edge. Each section below re-applies its own spacing.
    margin: calc(-1 * #{$portal-section-py}) calc(-1 * #{$portal-section-px}) -32px;

    @media (min-width: 768px) {
        margin: -24px -32px -32px;
    }

    &__title {
        font-size: $text-portal-body-lg;
        font-weight: 700;
        color: $portal-text;
        margin: 0;
        // Clear room above so the title doesn't crowd the mobile drag
        // handle / desktop close button.
        padding: 28px 20px 8px;
    }

    &__search {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 20px;
        border-bottom: 1px solid $portal-border;
    }

    &__search-icon {
        width: 16px;
        height: 16px;
        color: $portal-text-mid;
        opacity: 0.6;
        flex-shrink: 0;
    }

    &__search-input {
        flex: 1;
        height: 32px;
        border: none;
        outline: none;
        font-size: $text-portal-caption;
        color: $portal-text;
        background: transparent;

        &::placeholder {
            color: $portal-text-mid;
        }
    }

    &__list {
        list-style: none;
        margin: 0;
        padding: 4px 12px 12px;
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        min-height: 120px;
        // Cap to ~half the viewport so the sheet never scrolls off-screen
        // on short desktop windows; mobile is governed by BottomSheetModal's
        // 80vh max-height.
        max-height: 50vh;
    }

    &__item {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 14px 12px;
        border-radius: $portal-card-radius;
        cursor: pointer;
        transition: background-color 0.1s ease;

        &:hover {
            background: $portal-bg;
        }

        &:focus-visible {
            outline: 2px solid $theme-primary;
            outline-offset: -2px;
        }
    }

    &__item-icon {
        flex-shrink: 0;
        color: $portal-text-mid;

        :deep(svg) {
            width: 20px;
            height: 20px;
        }
    }

    // DS-2874: reserved-space placeholder for rows where the role gate
    // suppresses the house icon. Width matches the rendered svg above so the
    // street column starts at the same x-coordinate for every row.
    &__item-icon--placeholder {
        display: inline-block;
        width: 20px;
        height: 20px;
    }

    &__item-info {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    &__item-street {
        font-size: $text-portal-body;
        font-weight: 600;
        color: $portal-text;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__item-subline {
        font-size: $text-portal-small;
        color: $portal-text-mid;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__check {
        width: 16px;
        height: 16px;
        color: $theme-primary;
        flex-shrink: 0;
    }

    &__empty {
        padding: 24px 0;
        text-align: center;
        font-size: $text-portal-caption;
        color: $portal-text-mid;
    }
}
</style>
